import { useState } from 'react'
import { Plus, Minus, Mail, Twitter, Instagram, Send, MessageCircle } from 'lucide-react'
import { COMPETITION_POLICY_NOTICE } from '../utils/competitions'

const FAQS = [
  {
    question: 'What is Scouta?',
    answer: 'Scouta is a football ecosystem that connects players, scouts, and clubs. Players manage their careers, Scouts discover talent, and Clubs recruit — all on one platform.'
  },
  {
    question: 'Who can use Scouta?',
    answer: 'Scouta is built for players, Scouts, Clubs, and academies. Each role gets a tailored dashboard with the right tools for their needs.'
  },
  {
    question: 'Can players use Scouta?',
    answer: 'Yes! Players are the central role on Scouta. You can build a complete career profile with stats, career history, match clips, achievements, and documents.'
  },
  {
    question: 'Can I explore players on Scouta?',
    answer: 'Yes. Scouts and Clubs can use Explore to search and filter players by position, age, stats, availability, and match footage.'
  },
  {
    question: 'Can Scouts message players?',
    answer: 'Yes. Scouts and Clubs can initiate conversations with players. Players can reply to messages they receive. Scout-to-Club messaging is not available.'
  },
  {
    question: 'How do trial invitations work?',
    answer: 'Clubs send trial invitations to players with details like date, venue, and notes. Players can accept or decline directly from their dashboard.'
  },
  {
    question: 'Can clubs create player profiles?',
    answer: 'Yes. Clubs and academies can create player profiles manually or import them in bulk using CSV or Excel. Players can later claim their profiles after verification.'
  },
  {
    question: 'What match data is allowed on Scouta?',
    answer: 'Only recognized, verifiable competitions and tournaments can be recorded. Friendly matches, training games, exhibitions, and unofficial fixtures are not permitted.'
  },
  {
    question: 'Is Scouta free?',
    answer: 'Scouta is currently in active development with free access. Pricing details will be shared as the platform evolves.'
  },
  {
    question: 'How do I get started?',
    answer: 'Create an account, choose your role (Player, Scout, or Club), and start building your profile or discovering talent.'
  }
]

interface SupportPageProps {
  onNavigate?: (view: string) => void
}

export default function SupportPage({ onNavigate }: SupportPageProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [agreedToPolicy, setAgreedToPolicy] = useState(false)
  const [formError, setFormError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!firstName.trim() || !lastName.trim() || !contactEmail.trim() || !message.trim()) {
      setFormError('Please fill in your name, email, and message.')
      return
    }

    if (!agreedToPolicy) {
      setFormError('Please agree to the Privacy Policy and Terms of Service before submitting.')
      return
    }

    const subject = `Scouta message from ${firstName.trim()} ${lastName.trim()}`
    const body = [
      `First name: ${firstName.trim()}`,
      `Last name: ${lastName.trim()}`,
      `Email: ${contactEmail.trim()}`,
      `Phone: ${phone.trim() || 'N/A'}`,
      '',
      'Message:',
      message.trim(),
    ].join('\n')

    window.location.href = `mailto:scoutasupport@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <div className="min-h-screen bg-[#050910] text-white">
      <div className="max-w-6xl mx-auto px-4 py-20 space-y-24">
        <section className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-display font-bold">How can we help?</h1>
          <p className="text-lg text-slate-200">Find answers about Scouta's player, Scout, and Club features, and how the platform works.</p>
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/5 px-6 py-5 text-left text-slate-100">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-green-300">Recording policy</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-200">
              {COMPETITION_POLICY_NOTICE}
            </p>
          </div>
        </section>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
              <MessageCircle className="text-green-500" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {FAQS.map((faq, index) => (
                <div
                  key={index}
                  className="surface-panel rounded-2xl border border-white/10 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-semibold">{faq.question}</span>
                    {openIndex === index ? (
                      <Minus className="w-5 h-5 text-green-500" />
                    ) : (
                      <Plus className="w-5 h-5 text-slate-300" />
                    )}
                  </button>
                  {openIndex === index && (
                    <div className="px-6 pb-6 text-slate-200 leading-relaxed animate-in fade-in slide-in-from-top-2 duration-300">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <div className="surface-panel-low p-8 rounded-3xl border border-white/10 space-y-8">
              <div className="space-y-4">
                <h2 className="text-2xl font-bold">Contact Us</h2>
                <p className="text-slate-200 text-sm">
                  Have a question that isn&apos;t answered here? Reach out to the Scouta team directly.
                </p>
              </div>

              <div className="space-y-6">
                <a
                  href="mailto:scoutasupport@gmail.com"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-green-500/10 group-hover:text-green-500 transition-colors">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-300 font-bold">Email Us</p>
                    <p className="font-semibold group-hover:text-green-500 transition-colors">support@scouta.live</p>
                  </div>
                </a>

                <a
                  href="https://x.com/useScouta"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-blue-500/10 group-hover:text-blue-500 transition-colors">
                    <Twitter className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-300 font-bold">Follow Us</p>
                    <p className="font-semibold group-hover:text-blue-500 transition-colors">@useScouta</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 group opacity-50 cursor-not-allowed">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center">
                    <Instagram className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-300 font-bold">Instagram</p>
                    <p className="font-semibold">Coming Soon</p>
                  </div>
                </div>
              </div>
            </div>

              <div className="bg-white text-slate-900 p-8 rounded-[32px] border border-white/10 shadow-2xl space-y-6">
                <div className="space-y-3">
                  <h3 className="text-3xl font-display font-bold text-black">Send Us a Message</h3>
                  <p className="text-sm text-slate-600">
                  Fill in the form below and we&apos;ll receive your message by email.
                  </p>
                </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {formError && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-green-500"
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-green-500"
                  />
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="Email address"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-green-500"
                  />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone Number"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-green-500"
                  />
                </div>

                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Message"
                  rows={6}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-green-500 resize-none"
                />

                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreedToPolicy}
                    onChange={(e) => setAgreedToPolicy(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                  />
                  <span>
                    I&apos;ve read and agree with{' '}
                    <button type="button" onClick={() => onNavigate?.('terms')} className="underline font-medium hover:text-green-600">
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button type="button" onClick={() => onNavigate?.('privacy')} className="underline font-medium hover:text-green-600">
                      Privacy Policy
                    </button>
                    .
                  </span>
                </label>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-black px-8 py-4 font-semibold text-white transition hover:bg-slate-800"
                >
                  <Send className="w-5 h-5" />
                  Submit
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
